import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaFilm, FaExternalLinkAlt, FaSync, FaCircle, FaCopy, FaCheckCircle } from "react-icons/fa";
import {
  colors,
  spacing,
  typography,
  radius,
  shadows,
  pageTitle,
  pageSubtitle,
  btnPrimary,
  btnSecondary,
  inputBase,
  cardBase,
} from "../../utils/designTokens";

/**
 * SuperAdmin > Herramientas > Editor de Reels
 *
 * Embebe el estudio de reels de Instagram (Remotion + CMS propio) que corre
 * como servidor local aparte, fuera de este repo:
 *   ~/Downloads/habitia-redes-sociales/habitia-reels  →  npm run cms  →  puerto 4400
 *
 * El servidor necesita la máquina local (renderiza video con Chrome headless y
 * usa las API keys de ElevenLabs/Anthropic/Pexels de su .env), por eso no se
 * deploya junto a la web: esta pantalla lo detecta y lo muestra en un iframe.
 * La URL es configurable por si algún día se hostea (se persiste en localStorage).
 */

const STORAGE_KEY = "habitia_editor_reels_url";
// Servidor del equipo en Cloud Run (la clave de acceso viaja en el enlace y
// deja cookie de 30 días). El servidor local es para producción pesada.
const URL_NUBE = "https://habitia-reels-xdbse2xsjq-uc.a.run.app/?clave=c47f119b65003ef9a6b9b196451f97e4";
const URL_LOCAL = "http://localhost:4400";
const URL_DEFAULT = URL_NUBE;
const COMANDO = "cd ~/Downloads/habitia-redes-sociales/habitia-reels && npm run cms";

const EditorReels = () => {
  const [url, setUrl] = useState(() => localStorage.getItem(STORAGE_KEY) || URL_DEFAULT);
  const [urlDraft, setUrlDraft] = useState(() => localStorage.getItem(STORAGE_KEY) || URL_DEFAULT);
  // "buscando" | "online" | "offline"
  const [estado, setEstado] = useState("buscando");
  const [iframeKey, setIframeKey] = useState(0);
  const [copiado, setCopiado] = useState(false);
  const vivo = useRef(true);

  // Sonda de disponibilidad: con mode "no-cors" la respuesta es opaca pero el
  // fetch resuelve solo si el servidor contesta — alcanza como ping.
  const sondear = useCallback(async (destino) => {
    try {
      await fetch(destino, { mode: "no-cors", cache: "no-store" });
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    vivo.current = true;
    let timer;
    const ciclo = async () => {
      const ok = await sondear(url);
      if (!vivo.current) return;
      setEstado((prev) => {
        if (ok) return "online";
        return prev === "buscando" ? "offline" : "offline";
      });
      // Mientras esté caído, reintenta solo cada 4 s.
      if (!ok) timer = setTimeout(ciclo, 4000);
    };
    setEstado("buscando");
    ciclo();
    return () => {
      vivo.current = false;
      clearTimeout(timer);
    };
  }, [url, sondear, iframeKey]);

  const guardarUrl = () => {
    const limpia = urlDraft.trim().replace(/\/+$/, "") || URL_DEFAULT;
    localStorage.setItem(STORAGE_KEY, limpia);
    setUrlDraft(limpia);
    setUrl(limpia);
    setIframeKey((k) => k + 1);
  };

  const copiarComando = async () => {
    try {
      await navigator.clipboard.writeText(COMANDO);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* clipboard no disponible: el usuario puede seleccionar el texto */
    }
  };

  return (
    <div style={styles.pantalla}>
      {/* Barra superior compacta: el editor necesita todo el alto posible */}
      <div style={styles.barra}>
        <div style={styles.tituloBloque}>
          <FaFilm size={18} color={colors.primary} aria-hidden="true" />
          <div>
            <h1 style={{ ...pageTitle, fontSize: typography.sizes.lg }}>Editor de Reels</h1>
            <p style={pageSubtitle}>Estudio de contenido para Instagram — guion IA, voz y render</p>
          </div>
        </div>
        <div style={styles.acciones}>
          <span style={styles.estadoChip} aria-live="polite">
            <FaCircle
              size={8}
              color={estado === "online" ? colors.success : estado === "buscando" ? colors.warning : colors.error}
              aria-hidden="true"
            />
            {estado === "online" ? "Conectado" : estado === "buscando" ? "Buscando…" : "Sin conexión"}
          </span>
          {estado === "online" && (
            <>
              <button
                type="button"
                style={btnSecondary}
                onClick={() => setIframeKey((k) => k + 1)}
                aria-label="Recargar el editor"
              >
                <FaSync size={12} aria-hidden="true" /> Recargar
              </button>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...btnSecondary, textDecoration: "none" }}
                aria-label="Abrir el editor en una pestaña nueva"
              >
                <FaExternalLinkAlt size={12} aria-hidden="true" /> Pestaña nueva
              </a>
            </>
          )}
        </div>
      </div>

      {estado === "online" ? (
        <iframe
          key={iframeKey}
          src={url}
          title="Editor de Reels de Habitia"
          style={styles.iframe}
          allow="clipboard-read; clipboard-write; autoplay"
        />
      ) : (
        <div style={styles.centro}>
          <div style={{ ...cardBase, ...styles.tarjetaOffline }}>
            <FaFilm size={40} color={colors.gray300} aria-hidden="true" />
            <h2 style={styles.offlineTitulo}>
              {url === URL_NUBE ? "Conectando con el estudio…" : "El estudio de reels no está corriendo"}
            </h2>
            {url === URL_NUBE ? (
              <p style={styles.offlineTexto}>
                El servidor de la nube arranca solo cuando alguien lo usa: la primera
                conexión del día puede tardar unos 30 segundos. Se conecta solo.
              </p>
            ) : (
              <>
                <p style={styles.offlineTexto}>
                  El editor corre como servidor local (renderiza video y usa tus API keys).
                  Abrí una terminal en tu Mac y ejecutá:
                </p>
                <div style={styles.comandoCaja}>
                  <code style={styles.comando}>{COMANDO}</code>
                  <button
                    type="button"
                    style={{ ...btnSecondary, padding: "6px 12px", flexShrink: 0 }}
                    onClick={copiarComando}
                    aria-label="Copiar comando"
                  >
                    {copiado ? (
                      <>
                        <FaCheckCircle size={12} color={colors.success} aria-hidden="true" /> Copiado
                      </>
                    ) : (
                      <>
                        <FaCopy size={12} aria-hidden="true" /> Copiar
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
            <p style={{ ...styles.offlineTexto, fontSize: typography.sizes.sm }}>
              Esta pantalla se conecta sola apenas el servidor responda
              {estado === "buscando" ? " (buscando…)" : " (reintentando cada 4 segundos…)"}.
            </p>
            <div style={{ display: "flex", gap: spacing[2] }}>
              <button
                type="button"
                style={url === URL_NUBE ? btnPrimary : btnSecondary}
                onClick={() => {
                  setUrlDraft(URL_NUBE);
                  localStorage.setItem(STORAGE_KEY, URL_NUBE);
                  setUrl(URL_NUBE);
                }}
              >
                ☁️ Servidor del equipo (nube)
              </button>
              <button
                type="button"
                style={url === URL_LOCAL ? btnPrimary : btnSecondary}
                onClick={() => {
                  setUrlDraft(URL_LOCAL);
                  localStorage.setItem(STORAGE_KEY, URL_LOCAL);
                  setUrl(URL_LOCAL);
                }}
              >
                💻 Mi Mac (local)
              </button>
            </div>
            <div style={styles.urlFila}>
              <label htmlFor="editor-reels-url" style={styles.urlLabel}>
                URL del editor
              </label>
              <input
                id="editor-reels-url"
                type="url"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && guardarUrl()}
                style={{ ...inputBase, flex: 1 }}
                placeholder={URL_DEFAULT}
              />
              <button type="button" style={btnPrimary} onClick={guardarUrl}>
                Conectar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  pantalla: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: colors.bgMain,
    fontFamily: typography.fontFamily,
  },
  barra: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing[4],
    padding: `${spacing[3]}px ${spacing[6]}px`,
    background: colors.bgCard,
    borderBottom: `1px solid ${colors.gray200}`,
    flexShrink: 0,
  },
  tituloBloque: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
  },
  acciones: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
  },
  estadoChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: spacing[2],
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    padding: "4px 12px",
    borderRadius: radius.full,
    background: colors.gray100,
  },
  iframe: {
    flex: 1,
    width: "100%",
    border: "none",
    minHeight: 0,
    background: colors.white,
  },
  centro: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing[6],
  },
  tarjetaOffline: {
    maxWidth: 560,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: spacing[3],
    padding: spacing[8],
    boxShadow: shadows.md,
  },
  offlineTitulo: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.black,
    margin: 0,
  },
  offlineTexto: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
    lineHeight: 1.5,
  },
  comandoCaja: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    width: "100%",
    padding: `${spacing[2]}px ${spacing[3]}px`,
    background: colors.gray50,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
  },
  comando: {
    fontFamily: typography.monoFamily,
    fontSize: typography.sizes.sm,
    color: colors.gray900,
    textAlign: "left",
    wordBreak: "break-all",
    flex: 1,
  },
  urlFila: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    width: "100%",
    marginTop: spacing[2],
  },
  urlLabel: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    flexShrink: 0,
  },
};

export default EditorReels;
